import React, { useContext, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import VisibilityIcon from "@material-ui/icons/VisibilityOutlined";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOffOutlined";

import { AuthContext } from "../../context/Auth/AuthContext";

const LOGO = (
  <svg viewBox="0 0 37.74 24.04" xmlns="http://www.w3.org/2000/svg">
    <path fill="#FFFFFF" d="M23.94,16.02h-5.99s6.47-10.92,6.47-10.92l-12.22.06-5.9,10.09-6.3-.04L9.09,0h18.08c2.82.01,4.56,3.09,3.1,5.5l-6.33,10.52Z" />
    <path fill="#FFFFFF" d="M13.8,8.02h5.99s-6.47,10.92-6.47,10.92l12.22-.06,5.9-10.09,6.3.04-9.09,15.21H9.53c-2.36-.01-3.82-2.59-2.6-4.61l6.86-11.41Z" />
  </svg>
);

const useStyles = makeStyles(theme => ({
  root: { minHeight: "100vh", display: "flex", background: "#fff" },
  // ---- painel de marca ----
  brand: {
    width: "46%",
    background: "linear-gradient(160deg,#2C353D 0%,#161B21 100%)",
    color: "#fff",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    padding: "40px 44px",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: { display: "none" },
  },
  logo: { display: "flex", alignItems: "center", gap: 12, position: "relative", zIndex: 1 },
  logoSvg: { width: 34, height: "auto" },
  logoText: { fontSize: 19, fontWeight: 600, letterSpacing: ".01em" },
  mid: { margin: "auto 0", maxWidth: 380, position: "relative", zIndex: 1 },
  midH: { fontSize: 27, lineHeight: 1.25, fontWeight: 600, margin: 0, letterSpacing: "-.01em" },
  acc: { color: theme.app.lime },
  midP: { fontSize: 14, color: "#aeb6bf", margin: "16px 0 0", lineHeight: 1.6 },
  foot: { fontSize: 12, color: "#727b85", position: "relative", zIndex: 1 },
  wm: { position: "absolute", right: -60, bottom: -50, width: 420, opacity: 0.05 },
  line: { position: "absolute", height: 1, transform: "rotate(-28deg)" },
  l1: { width: 300, top: 120, left: -40, background: "rgba(206,255,0,.18)" },
  l2: { width: 200, top: 360, left: 120, background: "rgba(151,185,188,.18)" },
  // ---- painel de form ----
  formCol: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  box: { width: 340, maxWidth: "100%" },
  h1: { fontSize: 21, fontWeight: 600, margin: "0 0 4px", color: theme.app.ink },
  sub: { fontSize: 13.5, color: theme.app.textSecondary, margin: "0 0 26px" },
  field: { marginBottom: 16 },
  label: { display: "block", fontSize: 12.5, fontWeight: 500, color: theme.app.ink, margin: "0 0 6px" },
  inp: {
    display: "flex",
    alignItems: "center",
    border: `1px solid ${theme.app.border}`,
    borderRadius: 8,
    padding: "0 12px",
    height: 42,
    background: "#fff",
    "&:focus-within": { borderColor: theme.app.dark, boxShadow: "0 0 0 3px rgba(206,255,0,.35)" },
  },
  inpErr: { borderColor: "#C0453B" },
  input: { border: "none", outline: "none", flex: 1, fontSize: 14, color: theme.app.ink, background: "transparent", fontFamily: "inherit" },
  eye: { color: theme.app.textTertiary, cursor: "pointer", display: "flex", padding: 2 },
  errText: { color: "#C0453B", fontSize: 12, marginTop: 5 },
  rowEnd: { display: "flex", justifyContent: "flex-end", margin: "-6px 0 18px" },
  link: { fontSize: 12.5, color: theme.app.dark, fontWeight: 500, textDecoration: "none", cursor: "pointer" },
  btn: {
    width: "100%",
    height: 44,
    background: theme.app.dark,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    "&:hover": { background: "#1f262c" },
    "&:disabled": { opacity: 0.6, cursor: "default" },
  },
  divider: { display: "flex", alignItems: "center", gap: 12, margin: "20px 0", color: theme.app.textTertiary, fontSize: 12, "&::before,&::after": { content: '""', flex: 1, height: 1, background: theme.app.border } },
  sso: { width: "100%", height: 42, background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 8, fontSize: 13.5, color: theme.app.ink, display: "flex", alignItems: "center", justifyContent: "center", gap: 9, cursor: "pointer", "&:hover": { background: theme.app.lineSoft } },
  footNote: { fontSize: 12, color: theme.app.textTertiary, textAlign: "center", marginTop: 26 },
  footLink: { color: theme.app.textSecondary, textDecoration: "none" },
}));

const GoogleG = () => (
  <svg width="17" height="17" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const LoginSchema = Yup.object().shape({
  email: Yup.string().email("E-mail inválido").required("Informe o e-mail"),
  password: Yup.string().required("Informe a senha"),
});

const Login = () => {
  const classes = useStyles();
  const { handleLogin } = useContext(AuthContext);
  const [showPass, setShowPass] = useState(false);

  return (
    <div className={classes.root}>
      <div className={classes.brand}>
        <div className={classes.logo}>
          <span className={classes.logoSvg}>{LOGO}</span>
          <span className={classes.logoText}>Numera</span>
        </div>

        <span className={`${classes.line} ${classes.l1}`} />
        <span className={`${classes.line} ${classes.l2}`} />

        <div className={classes.mid}>
          <h2 className={classes.midH}>
            A plataforma pra construir <span className={classes.acc}>voz, SMS e WhatsApp.</span>
          </h2>
          <p className={classes.midP}>Monte fluxos, conecte números e provisione ramais SIP — tudo num lugar só.</p>
        </div>

        <div className={classes.foot}>© 2026 Numera · studio.numera.io</div>

        <span className={classes.wm}>{LOGO}</span>
      </div>

      <div className={classes.formCol}>
        <div className={classes.box}>
          <h1 className={classes.h1}>Entrar na sua conta</h1>
          <p className={classes.sub}>Acesse o Numera Builder.</p>

          <Formik
            initialValues={{ email: "", password: "" }}
            validationSchema={LoginSchema}
            onSubmit={(values, actions) => {
              handleLogin(values).finally(() => actions.setSubmitting(false));
            }}
          >
            {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
              <Form>
                <div className={classes.field}>
                  <label className={classes.label}>E-mail</label>
                  <div className={`${classes.inp} ${touched.email && errors.email ? classes.inpErr : ""}`}>
                    <input
                      className={classes.input}
                      type="email"
                      name="email"
                      placeholder="voce@empresa.com"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoFocus
                    />
                  </div>
                  {touched.email && errors.email && <div className={classes.errText}>{errors.email}</div>}
                </div>

                <div className={classes.field}>
                  <label className={classes.label}>Senha</label>
                  <div className={`${classes.inp} ${touched.password && errors.password ? classes.inpErr : ""}`}>
                    <input
                      className={classes.input}
                      type={showPass ? "text" : "password"}
                      name="password"
                      placeholder="••••••••"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <span className={classes.eye} onClick={() => setShowPass(s => !s)}>
                      {showPass ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </span>
                  </div>
                  {touched.password && errors.password && <div className={classes.errText}>{errors.password}</div>}
                </div>

                <div className={classes.rowEnd}>
                  <span className={classes.link} onClick={() => toast.info("Recuperação de senha em breve.")}>
                    Esqueci minha senha
                  </span>
                </div>

                <button type="submit" className={classes.btn} disabled={isSubmitting}>
                  Entrar
                </button>
              </Form>
            )}
          </Formik>

          <div className={classes.divider}>ou</div>
          <div className={classes.sso} onClick={() => toast.info("Login com Google em breve.")}>
            <GoogleG /> Continuar com Google
          </div>

          <div className={classes.footNote}>
            Não tem conta? <a className={classes.footLink} href="mailto:contato@numera.io">Fale com seu administrador</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
